/**
 * Unified typed form for all follow-up encounters (Wellness / CoCM / Unbilled).
 *
 * Steps: Type (hidden in activity/edit) → Assessments → Goals (progress +
 * creation) → Notes (general) → Details → (Next-Task Assignment when
 * activity-driven).
 *
 * The persisted follow-up code (the "billing reality") is PREFILLED by path,
 * then confirmed by the clinician in the Billing section of the Details step —
 * placed directly above the care-time log, so the track is affirmed right
 * before the time it applies to is logged. Prefill sources:
 *  - edit → the existing encounter's tag;
 *  - activity → the activity's mapped code (e.g. `follow-up-on-cocm` →
 *    `cocm-follow-up-interaction`), so the default matches the task that
 *    generated it;
 *  - free creation → the member's active care plan (an active CoCM plan means
 *    a CoCM followup), falling back to their intake track for the Wellness vs
 *    Unbilled distinction — see `resolveFollowUpCodePrefill`.
 *
 * The selector is offered on every path: the prefill carries the default, and
 * the clinician gets to affirm or correct it. Goal progress is always
 * available.
 *
 * @agentic connections
 */

import { Alert, Skeleton, Stack } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { useMedplum } from "@medplum/react";
import { forwardRef, useEffect, useMemo, useState } from "react";
import type { Encounter } from "@medplum/fhirtypes";

import {
  ACTIVITY_ENCOUNTER_TYPE_MAP,
  getCarePlanActivityDisplay,
  isFollowUpEncounterType,
  resolveFollowUpCodePrefill,
  VALUE_SET,
} from "@benchmark/core/constants";

import {
  FormField,
  FormStep,
  SteppedForm,
} from "@benchmark/ui/components/atomic";
import { CallDocumentationField } from "@/components/domain/care-time/CallDocumentationField";
import { useCallTimeCareSelection } from "../hooks/useCallTimeCareSelection";
import { usePatientCarePlans } from "@/hooks/domain/usePatientCarePlans";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { findMostRecentIntakeEncounter } from "@benchmark/core/services";
import type {
  EncounterActivityContext,
  FollowUpInteractionFormData,
} from "@benchmark/core/types";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";

import type { EncounterFormRef } from "../EncounterFormShell";
import { useNonIntakeFormScaffold } from "../hooks/useNonIntakeFormScaffold";
import {
  FollowUpBillingRealitySelect,
  FollowUpEnrollmentBanner,
  type FollowUpEnrollment,
} from "../sections/FollowUpBillingRealitySection";
import { GoalProgressStep } from "../shared/GoalProgressStep";
import {
  AssessmentsStep,
  validateAssessmentsStep,
} from "../shared/AssessmentsStep";
import { EncounterDetailsStep } from "../shared/EncounterDetailsStep";
import { EncounterTypeStep } from "../shared/EncounterTypeStep";
import { GeneralNoteStep } from "../shared/GeneralNoteStep";
import { NextTaskAssignmentStep } from "../shared/NextTaskAssignmentStep";
import {
  buildDirectionOptions,
  buildMethodOptions,
} from "../shared/encounterIcons";
import { useEncounterUpsertSubmit } from "./useEncounterUpsertSubmit";

const METHOD_OPTIONS = buildMethodOptions();
const DIRECTION_OPTIONS = buildDirectionOptions();

type FollowUpEncounterCode =
  | "clinical-follow-up-interaction"
  | "cocm-follow-up-interaction"
  | "unbilled-follow-up-interaction";

const FOLLOW_UP_CODES: FollowUpEncounterCode[] = [
  "clinical-follow-up-interaction",
  "cocm-follow-up-interaction",
  "unbilled-follow-up-interaction",
];

/** Read the follow-up encounter-type tag from an existing encounter. */
const readFollowUpTypeTag = (
  encounter: Encounter | undefined,
): FollowUpEncounterCode | undefined => {
  const code = encounter?.meta?.tag?.find(
    (t) => t.system === VALUE_SET.ENCOUNTER_TYPE,
  )?.code;
  return code && (FOLLOW_UP_CODES as string[]).includes(code)
    ? (code as FollowUpEncounterCode)
    : undefined;
};

const validateGoals = (
  values: FollowUpInteractionFormData,
): Record<string, string> | null => {
  const errors: Record<string, string> = {};
  const progress = values["goal-progress"] ?? [];
  progress.forEach((entry, index) => {
    const hasDate =
      !!entry.effectiveDateTime &&
      (typeof entry.effectiveDateTime === "string"
        ? entry.effectiveDateTime.trim() !== ""
        : true);
    const hasGasScore = entry.gasScore !== undefined && entry.gasScore !== null;
    if (!hasDate && !hasGasScore) {
      return;
    }
    const prefix = `goal-progress.${entry.goalId || index}`;
    if (!hasGasScore) {
      errors[`${prefix}.gasScore`] =
        "GAS score is required for all goal progress updates";
    }
    if (hasGasScore && (entry.gasScore < -2 || entry.gasScore > 2)) {
      errors[`${prefix}.gasScore`] = "GAS score must be between -2 and +2";
    }
    if (!hasDate) {
      errors[`${prefix}.effectiveDateTime`] =
        "Date is required for all goal progress updates";
    }
  });
  return Object.keys(errors).length > 0 ? errors : null;
};

/**
 * Props for `FollowUpInteractionForm`.
 * @agentic connections
 */
export type FollowUpInteractionFormProps = {
  patientId?: string;
  encounter?: Encounter;
  onSuccess?: (encounterId: string) => void;
  onCancel?: () => void;
  activityContext?: EncounterActivityContext;
  restrictToEncounterTypes?: string[];
  nextActivityLabel?: string;
  nextActivityDefaultOwner?: { ref: string; display: string };
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  onNextTaskOwnerOverrideChange?: (
    owner: { ref: string; display: string } | null,
  ) => void;
  nextActivityAppliedRule?: AppliedBusinessRule;
  isResolvingNextTaskOwner?: boolean;
};

/**
 * Unified follow-up form for `clinical-`, `cocm-`, and
 * `unbilled-follow-up-interaction`.
 * @agentic connections
 */
export const FollowUpInteractionForm = forwardRef<
  EncounterFormRef,
  FollowUpInteractionFormProps
>(function FollowUpInteractionForm(
  {
    patientId: propPatientId,
    encounter: propEncounter,
    onSuccess,
    onCancel,
    activityContext,
    restrictToEncounterTypes,
    nextActivityLabel,
    nextActivityDefaultOwner,
    nextTaskOwnerOverride,
    onNextTaskOwnerOverrideChange,
    nextActivityAppliedRule,
    isResolvingNextTaskOwner = false,
  },
  ref,
) {
  const medplum = useMedplum();

  const scaffoldType: FollowUpEncounterCode =
    readFollowUpTypeTag(propEncounter) ?? "clinical-follow-up-interaction";

  const form = useForm<FollowUpInteractionFormData>({
    initialValues: {
      "encounter-type": scaffoldType,
      period: { start: "", end: "" },
      "encounter-method": "",
      "encounter-inout": "",
      comment: "",
      "goal-progress": [],
      selectedQuestionnaires: [],
      questionnaireResponses: {},
    },
  });

  const {
    patient,
    existingEncounter,
    isEditing,
    isLoadingEdit,
    loadError,
    practitionerRoles,
    ConfirmationModal,
    markAsClean,
    originalFormData,
  } = useNonIntakeFormScaffold({
    type: scaffoldType,
    form,
    propPatientId,
    propEncounter,
    activityContext,
    nextTaskOwnerOverride,
    ref,
  });

  // Prefill the persisted follow-up code: edit → existing tag; activity → the
  // activity's mapped follow-up code; free creation → the member's active care
  // plan, falling back to their intake track. The clinician confirms it in the
  // Billing section of the Details step.
  const [followUpCode, setFollowUpCode] =
    useState<FollowUpEncounterCode>(scaffoldType);
  // Whether the persisted follow-up code has been resolved. The form is gated
  // on this (see loading guard) so it cannot be submitted with the initial
  // default before the async care-plan / intake lookups settle — otherwise a
  // fast save could persist the wrong follow-up code + care-time method.
  const [codeResolved, setCodeResolved] = useState(false);
  const patientId = patient?.id;

  // Advisory enrollment context, and the CoCM axis of the free-creation
  // prefill. A failed search resolves to an empty list, so the prefill
  // degrades to the intake track rather than gating the wizard forever.
  const {
    cocmCarePlan,
    triageCarePlans,
    loading: carePlansLoading,
  } = usePatientCarePlans(patientId);
  const hasActiveCoCMCarePlan = !!cocmCarePlan;
  const enrollment: FollowUpEnrollment = carePlansLoading
    ? undefined
    : hasActiveCoCMCarePlan
      ? "cocm"
      : triageCarePlans.length > 0
        ? "triage"
        : "none";

  // Guarded on the resolved STATE (not a start-once ref) so the effect is
  // re-runnable: if a dependency changes — or the effect is torn down and
  // remounted (React StrictMode) — before the in-flight intake lookup settles,
  // the cleanup cancels the stale lookup and the effect restarts. The gate can
  // therefore never stick unresolved.
  //
  // Once resolved it stays resolved: `usePatientCarePlans` refetches on care-plan
  // events, and re-running the prefill then would clobber a track the clinician
  // had already chosen. The banner keeps reporting live enrollment, so it may
  // diverge from the selector — the same advisory-vs-reality split the intake
  // form has, where the eligibility banner is deliberately free to disagree with
  // the clinician's choice.
  useEffect(() => {
    if (codeResolved) return;
    if (!patientId) return;

    if (existingEncounter) {
      // Edit: mirror the existing encounter's tag. Encounters routed here
      // always carry a follow-up tag; if one is somehow absent, keep the
      // seeded default rather than leave the form gated forever.
      const tagCode = readFollowUpTypeTag(existingEncounter);
      if (tagCode) {
        setFollowUpCode(tagCode);
        form.setFieldValue("encounter-type", tagCode);
      }
      setCodeResolved(true);
      return;
    }

    // Activity-driven follow-up: fix the code to the activity's mapped
    // follow-up code (e.g. `follow-up-on-cocm` → `cocm-follow-up-interaction`)
    // so its billing track + care-time method match the task that generated
    // it, regardless of the member's prior intake track.
    const activityMapped = activityContext?.activityCode
      ? ACTIVITY_ENCOUNTER_TYPE_MAP[activityContext.activityCode]
      : undefined;
    if (
      typeof activityMapped === "string" &&
      isFollowUpEncounterType(activityMapped)
    ) {
      const code = activityMapped as FollowUpEncounterCode;
      setFollowUpCode(code);
      form.setFieldValue("encounter-type", code);
      setCodeResolved(true);
      return;
    }

    // Free creation: the member's care plan decides the CoCM axis, so wait for
    // it to load before prefilling anything.
    if (carePlansLoading) return;

    let cancelled = false;
    const resolve = (code: FollowUpEncounterCode) => {
      setFollowUpCode(code);
      form.setFieldValue("encounter-type", code);
      setCodeResolved(true);
    };

    // An active CoCM plan settles the track on its own — skip the intake
    // lookup entirely.
    if (hasActiveCoCMCarePlan) {
      resolve(
        resolveFollowUpCodePrefill({
          hasActiveCoCMCarePlan: true,
          intakeCode: undefined,
        }) as FollowUpEncounterCode,
      );
      return;
    }

    // No active CoCM plan: the member's intake track resolves Wellness vs
    // Unbilled.
    (async () => {
      try {
        const intake = await findMostRecentIntakeEncounter(medplum, patientId);
        if (cancelled) return;
        const intakeCode = intake?.meta?.tag?.find(
          (t) => t.system === VALUE_SET.ENCOUNTER_TYPE,
        )?.code;
        resolve(
          resolveFollowUpCodePrefill({
            hasActiveCoCMCarePlan: false,
            intakeCode,
          }) as FollowUpEncounterCode,
        );
      } catch {
        // Do not log the raw error — a patient-scoped Medplum error can carry
        // resource identifiers. Fall back to the seeded Wellness default.
        console.error("Failed to resolve follow-up code from intake");
        if (!cancelled) resolve(scaffoldType);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `form` intentionally omitted (new object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    codeResolved,
    patientId,
    existingEncounter,
    medplum,
    activityContext?.activityCode,
    carePlansLoading,
    hasActiveCoCMCarePlan,
  ]);

  // Keep the persisted tag, the submit discriminator, and the billed
  // care-time track in sync, so a CoCM followup can never be stamped
  // `task-completion` (or vice versa).
  const handleBillingRealityChange = (code: string) => {
    if (!(FOLLOW_UP_CODES as string[]).includes(code)) return;
    const followUp = code as FollowUpEncounterCode;
    form.setFieldValue("encounter-type", followUp);
    setFollowUpCode(followUp);
  };

  const careTime = useCallTimeCareSelection();

  const handleSubmit = useEncounterUpsertSubmit<FollowUpEncounterCode>({
    type: followUpCode,
    careTimeMethod:
      followUpCode === "cocm-follow-up-interaction"
        ? "cocm-followup"
        : "task-completion",
    patient,
    existingEncounter,
    originalFormData,
    practitionerRoles,
    activityContext,
    onSuccess,
    onSubmitted: markAsClean,
    getCareTimeMinutes: careTime.getCareTimeMinutes,
    getCareTimeCommunicationId: careTime.getCareTimeCommunicationId,
    getCareTimeEffectivePeriod: careTime.getCareTimeEffectivePeriod,
  });

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();

  const steps = useMemo(
    () => [
      {
        id: "type",
        title: "Type",
        subtitle: isEditing
          ? "Interaction type. (Read-Only)"
          : "Select the type of interaction.",
        readOnly: isEditing,
        showWhen: () =>
          !activityContext?.activityCode &&
          !isEditing &&
          !(restrictToEncounterTypes && restrictToEncounterTypes.length === 1),
        requiredFields: ["encounter-type"] as string[],
      },
      {
        id: "assessments",
        title: "Assessments",
        subtitle: "Administer standardized assessments.",
        optional: true,
        validate: validateAssessmentsStep,
      },
      {
        id: "goals",
        title: "Goals",
        subtitle: "Create or track progress on treatment goals.",
        optional: true,
        validate: validateGoals,
      },
      {
        id: "notes",
        title: "Notes",
        subtitle: "Document the encounter",
        validate: (values: FollowUpInteractionFormData) =>
          !values.comment || values.comment.trim() === ""
            ? { comment: "General note is required" }
            : null,
      },
      {
        id: "details",
        title: "Details",
        subtitle: "Review and confirm the interaction details.",
        requiredFields: [
          "period.start",
          "period.end",
          "encounter-method",
          "encounter-inout",
        ] as string[],
      },
      ...(activityContext
        ? [
            {
              id: "next-task-assignment",
              title: "Assignment",
              subtitle: "Review next task assignment",
            },
          ]
        : []),
    ],
    [activityContext, isEditing, restrictToEncounterTypes],
  );

  const submitText = activityContext?.activityCode
    ? getCarePlanActivityDisplay(activityContext.activityCode)
    : isEditing
      ? "Update Interaction"
      : "Save Interaction";

  if (loadError) {
    return (
      <div data-testid="follow-up-interaction-form">
        <Alert
          color="red"
          icon={<IconAlertTriangle size={18} />}
          title="Couldn't load this interaction"
          data-testid="follow-up-load-error"
        >
          We couldn&apos;t load the saved details for this interaction. Refresh
          the page to try again — previous documentation has not been changed.
        </Alert>
        <ConfirmationModal />
      </div>
    );
  }

  if (!patient || isLoadingEdit || !codeResolved) {
    return (
      <div data-testid="follow-up-interaction-form">
        <Stack gap="md">
          <Skeleton height={32} width="40%" />
          <Skeleton height={36} />
          <Skeleton height={44} width="200px" />
        </Stack>
        <ConfirmationModal />
      </div>
    );
  }

  return (
    <div data-testid="follow-up-interaction-form">
      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={createSubmissionHandler(handleSubmit)}
        isSubmitting={isSubmitting}
        submitText={submitText}
        onCancel={onCancel}
      >
        <FormStep id="type">
          <EncounterTypeStep
            value={form.values["encounter-type"]}
            allowedTypes={restrictToEncounterTypes ?? FOLLOW_UP_CODES}
            error={form.errors["encounter-type"] as string | undefined}
            readOnly={isEditing}
            onChange={handleBillingRealityChange}
          />
        </FormStep>

        <FormStep id="assessments">
          <AssessmentsStep
            form={form}
            patientBirthDate={patient.birthDate}
            patientId={patient.id}
            currentEncounterId={existingEncounter?.id}
            currentEncounterStatus={existingEncounter?.status}
          />
        </FormStep>

        <FormStep id="goals">
          <GoalProgressStep patientId={patient.id} form={form} />
        </FormStep>

        <FormStep id="notes">
          <GeneralNoteStep form={form} />
        </FormStep>

        <FormStep id="details">
          <Stack gap="md">
            <EncounterDetailsStep
              form={form}
              methodOptions={METHOD_OPTIONS}
              directionOptions={DIRECTION_OPTIONS}
              billableOptions={[]}
              requiresBillable={false}
            />
            {/* Enrollment advisory + billing-track selector sit directly above
                the care-time log (not at the top of the step), so the
                clinician affirms the billing track right before logging the
                time it applies to. Mirrors the intake form's Billing
                section. */}
            <FollowUpEnrollmentBanner enrollment={enrollment} />
            <FormField
              label="Billing"
              variant="section"
              showDivider={false}
              description="Confirm how this followup is billed. This sets how the encounter is reported and how the logged time is attributed — whether the time bills as CoCM is driven by the member's active CoCM episode, not by this choice."
            >
              <FollowUpBillingRealitySelect
                value={form.values["encounter-type"]}
                onChange={handleBillingRealityChange}
              />
            </FormField>
            {!activityContext && (
              <CallDocumentationField
                patientId={patient?.id}
                minutes={careTime.minutes}
                onMinutesChange={careTime.setMinutes}
                selectedCommunicationId={careTime.selectedCall?.communicationId}
                onSelectCall={careTime.selectCall}
              />
            )}
          </Stack>
        </FormStep>

        <FormStep id="next-task-assignment">
          <Stack gap="md">
            <NextTaskAssignmentStep
              form={form}
              nextActivityLabel={nextActivityLabel}
              nextActivityDefaultOwner={nextActivityDefaultOwner}
              nextTaskOwnerOverride={nextTaskOwnerOverride}
              onNextTaskOwnerOverrideChange={onNextTaskOwnerOverrideChange}
              nextActivityAppliedRule={nextActivityAppliedRule}
              isResolvingNextTaskOwner={isResolvingNextTaskOwner}
            />
            <CallDocumentationField
              patientId={patient?.id}
              minutes={careTime.minutes}
              onMinutesChange={careTime.setMinutes}
              selectedCommunicationId={careTime.selectedCall?.communicationId}
              onSelectCall={careTime.selectCall}
            />
          </Stack>
        </FormStep>
      </SteppedForm>

      <ConfirmationModal />
    </div>
  );
});
